import dayjs from 'dayjs';

const WORK_START_MINUTES = 7 * 60;
const WORK_END_MINUTES = 23 * 60;

export const timeToMinutes = (value) => {
  if (!value || typeof value !== 'string') return 0;
  const [hours, minutes] = value.split(':').map(Number);
  if (Number.isNaN(hours)) return 0;
  return hours * 60 + (Number.isNaN(minutes) ? 0 : minutes);
};

export const formatClock = (value) => {
  if (!value || typeof value !== 'string') return '';
  return value.slice(0, 5);
};

const clipRange = (start, end, min, max) => {
  const from = Math.max(start, min);
  const to = Math.min(end, max);
  return to > from ? to - from : 0;
};

const getTodayKey = (appointments = {}) => {
  const days = appointments || {};
  const today = dayjs().format('YYYY-MM-DD');
  if (days[today]) return today;
  return Object.keys(days).find((key) => dayjs(key).isSame(dayjs(), 'day')) || today;
};

export const getGreetingKey = (hour = dayjs().hour()) => {
  if (hour < 13) return 'greetingMorning';
  if (hour < 18) return 'greetingAfternoon';
  return 'greetingEvening';
};

export const buildChairOccupancy = (workers = [], appointments) => {
  const days = appointments || {};
  const dayKey = getTodayKey(days);
  const dayAppointments = days[dayKey] || [];
  const workersList = Array.isArray(workers) ? workers : [];
  if (!workersList.length) return [];

  const tasksByWorker = Object.create(null);
  for (let i = 0; i < dayAppointments.length; i += 1) {
    const task = dayAppointments[i];
    if (!task?.idprenotazione) continue;
    const id = task.idpoltrona;
    if (tasksByWorker[id] == null) tasksByWorker[id] = [];
    tasksByWorker[id].push(task);
  }

  return workersList.map((worker) => {
    const tasks = tasksByWorker[worker.idpoltrona] || [];
    const closures =
      worker.chiusure?.[dayKey] ||
      worker.chiusure?.[dayjs(dayKey).format('DD/MM/YYYY')] ||
      [];
    let available = WORK_END_MINUTES - WORK_START_MINUTES;

    for (let i = 0; i < closures.length; i += 1) {
      const closure = closures[i];
      const from = timeToMinutes(closure.dalle);
      const to = closure.alle === '00:00:00' ? 24 * 60 : timeToMinutes(closure.alle);
      available -= clipRange(from, to, WORK_START_MINUTES, WORK_END_MINUTES);
    }

    let occupied = 0;
    for (let i = 0; i < tasks.length; i += 1) {
      const task = tasks[i];
      const from = timeToMinutes(task.orario_start);
      const to = task.orario_end === '00:00:00' ? 24 * 60 : timeToMinutes(task.orario_end);
      occupied += clipRange(from, to, WORK_START_MINUTES, WORK_END_MINUTES);
    }

    const closed = available <= 0;
    const percent = closed ? 0 : Math.min(100, Math.round((occupied / available) * 100));
    const soldOut = !closed && occupied >= available && available > 0;

    return {
      id: worker.idpoltrona,
      name: worker.nome_referente || '',
      bookings: tasks.length,
      percent: soldOut ? 100 : percent,
      soldOut,
      closed,
    };
  });
};

export const buildUpcomingAppointments = (workers = [], appointments, limit) => {
  const days = appointments || {};
  const dayKey = getTodayKey(days);
  const dayList = days[dayKey] || [];
  if (!dayList.length) return [];

  const now = dayjs().hour() * 60 + dayjs().minute();
  const names = Object.create(null);
  const workersList = Array.isArray(workers) ? workers : [];
  for (let i = 0; i < workersList.length; i += 1) {
    const worker = workersList[i];
    names[worker.idpoltrona] = worker.nome_referente;
  }

  const upcoming = [];
  for (let i = 0; i < dayList.length; i += 1) {
    const task = dayList[i];
    if (!task?.idprenotazione) continue;
    const endMinutes =
      task.orario_end === '00:00:00' ? 24 * 60 : timeToMinutes(task.orario_end);
    if (endMinutes < now) continue;

    upcoming.push({
      id: task.idprenotazione,
      time: formatClock(task.orario_start),
      startMinutes: timeToMinutes(task.orario_start),
      endMinutes,
      ongoing: timeToMinutes(task.orario_start) <= now && endMinutes >= now,
      clientName: `${task.nome || ''} ${task.cognome || ''}`.trim(),
      clientId: task.idutente || null,
      phoneNumber: task.cellulare || task.telefono || '',
      workerName: names[task.idpoltrona] || '',
      service: task.servizi?.[0]?.nome || '',
    });
  }

  upcoming.sort((a, b) => a.startMinutes - b.startMinutes);

  if (typeof limit === 'number') {
    return upcoming.slice(0, limit);
  }

  return upcoming;
};
